import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter } from 'events';
import { getUrl } from './utils';
import { JWTTokenService } from '../_services/jwtToken.service';

export abstract class BaseService {
  protected static clearAllCacheRequested = new EventEmitter();
  private cache = {};

  constructor(
    protected httpClient: HttpClient,
    protected jwtTokenService: JWTTokenService) {
    BaseService.clearAllCacheRequested.addListener(null, () => {
      this.cache = {};
    });
  }

  /**
   * Clears the cache in all derived instances. Used when a user logs on/off.
   */
  public static GlobalClearCache() {
    BaseService.clearAllCacheRequested.emit(null);
  }

  /**
   * Removes the resource with the specified uri from the cache
   * @param uri The uri that identifies the chached resource
   */
  public RemoveFromCache(uri: string) {
    delete this.cache[uri];
  }

  /**
   * Clears the cache of the service.
   */
  public ClearCache() {
    this.cache = {};
  }

  protected CachedCall<T>(requestId: string, get$: Observable<T>): Observable<T> {
    if (Object.keys(this.cache).indexOf(requestId) !== -1) {
      return of(this.cache[requestId]);
    } else {
      return get$.pipe(map(res => {
        this.cache[requestId] = res;
        return res;
      }));
    }
  }

  protected HttpPost<T>(uri: string, model: any, authenticate = true, params: HttpParams = null): Observable<T> {
    this.ClearCache(); // for simplicity sake, we're clearing all cache on each DELETE/PUT/POST. This is erring on the side of caution, but can be optimized to leave unaffected parts of the cache if required.
    const fullUrl = getUrl(uri);
    return authenticate ? this.httpClient.post<T>(fullUrl, model, this.buildOptions(authenticate, params)) : this.httpClient.post<T>(fullUrl, model);
  }

  protected HttpPut<T>(uri: string, model: any, authenticate = true, params: HttpParams = null): Observable<T> {
    this.ClearCache(); // for simplicity sake, we're clearing all cache on each DELETE/PUT/POST. This is erring on the side of caution, but can be optimized to leave unaffected parts of the cache if required.
    const fullUrl = getUrl(uri);
    return authenticate ? this.httpClient.put<T>(fullUrl, model, this.buildOptions(authenticate, params)) : this.httpClient.put<T>(fullUrl, model);
  }

  protected HttpDelete<T>(uri: string, id?: number, authenticate = true): Observable<T> {
    this.ClearCache(); // for simplicity sake, we're clearing all cache on each DELETE/PUT/POST. This is erring on the side of caution, but can be optimized to leave unaffected parts of the cache if required.
    const fullUrl = id ? getUrl(`${uri}/${id}`) : getUrl(uri);
    return authenticate ? this.httpClient.delete<T>(fullUrl, this.buildOptions()) : this.httpClient.delete<T>(fullUrl);
  }

  protected InvalidateCache(uriPattern: RegExp | string) {
    const regex: RegExp = uriPattern instanceof RegExp ? uriPattern : new RegExp(uriPattern);
    Object.keys(this.cache)
      .filter(k => regex.test(k))
      .forEach(k => delete this.cache[k]);
  }

  // todo: replace existing calls to CachedGet with calls to CachedHttpGet, but make sure to also update the uri's in the ClearCache methods
  protected CachedHttpGet<T>(uri: string, authenticate = true): Observable<T> {
    if (Object.keys(this.cache).indexOf(uri) !== -1) {
      return of(this.cache[uri]);
    } else {
      const get$ = this.HttpGet<T>(uri, authenticate);
      return get$.pipe(map(res => {
        this.cache[uri] = res;
        return res;
      }));
    }
  }

  protected HttpGet<T>(uri: string, authenticate = true, params: HttpParams = null): Observable<T> {
    const fullUrl = getUrl(uri);
    return this.httpClient.get<T>(fullUrl, this.buildOptions(authenticate, params));
  }

  protected buildOptions(authenticate: boolean = true, params: HttpParams = null): { headers?: HttpHeaders; params?: HttpParams } {
    const options: any = {};
    if (authenticate) {
      options.headers = new HttpHeaders().set('Content-type', 'application/json').append('Authorization', 'Bearer ' + this.jwtTokenService.getToken('portal'));
    }
    if (params) {
      options.params = params;
    }
    return options;
  }
}
